/**
 * Single source of truth for feature visibility across the platform.
 * Both NavHeader and DashboardPage consume this config.
 */

export type StudentGrade = "GRADE_9" | "GRADE_10" | "GRADE_11" | "GRADE_12" | "GAP_YEAR";
export type StudentType = "UNDERGRADUATE" | "GRADUATE";

export interface StudentContext {
  studentType: StudentType;
  grade: StudentGrade | null;
  isInternational: boolean;
}

export type FeatureCategory =
  | "getting_started"
  | "planning"
  | "application"
  | "financial"
  | "guidance"
  | "international"
  | "account";

export interface FeatureConfig {
  id: string;
  path: string;
  label: string;
  gradLabel?: string;
  icon: string;
  category: FeatureCategory;
  description: string;
  isVisible: (ctx: StudentContext) => boolean;
  isPrimaryNav?: boolean;
}

// Helpers
const gradeNum: Record<string, number> = {
  GRADE_9: 9, GRADE_10: 10, GRADE_11: 11, GRADE_12: 12, GAP_YEAR: 13,
};
function gradeAtLeast(ctx: StudentContext, min: number): boolean {
  if (ctx.studentType === "GRADUATE") return min <= 13;
  return (gradeNum[ctx.grade || ""] ?? 0) >= min;
}
const isGrad = (ctx: StudentContext) => ctx.studentType === "GRADUATE";
const isUndergrad = (ctx: StudentContext) => ctx.studentType === "UNDERGRADUATE";

export const FEATURES: FeatureConfig[] = [
  // ─── Getting Started ───────────────────────────────────
  {
    id: "roadmap", path: "/roadmap", label: "Roadmap", icon: "\ud83d\uddfa\ufe0f",
    category: "getting_started", description: "Your personalized journey plan",
    isVisible: () => true, isPrimaryNav: true,
  },
  {
    id: "discovery", path: "/discovery", label: "Discovery", icon: "\ud83e\udde0",
    category: "getting_started", description: "Assess your strengths and interests",
    isVisible: () => true,
  },
  {
    id: "progress", path: "/progress", label: "Progress", icon: "\ud83d\udcca",
    category: "getting_started", description: "Track your overall progress",
    isVisible: () => true,
  },

  // ─── Planning ──────────────────────────────────────────
  {
    id: "colleges", path: "/colleges", label: "College Search", gradLabel: "Program Search", icon: "\ud83c\udfe0",
    category: "planning", description: "Find schools that fit you",
    isVisible: () => true, isPrimaryNav: true,
  },
  {
    id: "college-list", path: "/colleges/list", label: "My College List", gradLabel: "My Program List", icon: "\ud83d\udccb",
    category: "planning", description: "Reach, target, and safety schools",
    isVisible: (ctx) => gradeAtLeast(ctx, 11),
  },
  {
    id: "test-strategy", path: "/test-strategy", label: "Test Strategy", gradLabel: "GRE/GMAT Prep", icon: "\ud83d\udcdd",
    category: "planning", description: isGrad.toString().includes("") ? "Prepare for standardized tests" : "SAT/ACT strategy and prep",
    isVisible: (ctx) => {
      if (isGrad(ctx)) return true;
      if (ctx.grade === "GAP_YEAR") return false;
      return true;
    },
  },
  {
    id: "early-strategy", path: "/early-strategy", label: "ED/EA Strategy", icon: "\ud83c\udfb2",
    category: "planning", description: "Early Decision and Early Action planning",
    isVisible: (ctx) => isUndergrad(ctx) && gradeAtLeast(ctx, 11) && ctx.grade !== "GAP_YEAR",
  },
  {
    id: "activities", path: "/activity-tracker", label: "Activities", icon: "\u26bd",
    category: "planning", description: "Track and rank your extracurriculars",
    isVisible: (ctx) => isUndergrad(ctx),
    isPrimaryNav: true,
  },
  {
    id: "passion-projects", path: "/passion-projects", label: "Passion Projects", icon: "\ud83d\ude80",
    category: "planning", description: "Build meaningful projects",
    isVisible: (ctx) => isUndergrad(ctx),
  },
  {
    id: "extracurriculars", path: "/extracurriculars", label: "Programs", icon: "\ud83c\udfc6",
    category: "planning", description: "Summer programs and opportunities",
    isVisible: (ctx) => isUndergrad(ctx),
  },

  // ─── Application ───────────────────────────────────────
  {
    id: "essays", path: "/essays", label: "Essays", gradLabel: "SOP Coach", icon: "\u270d\ufe0f",
    category: "application", description: isGrad.toString().includes("") ? "Craft your application essays" : "",
    isVisible: (ctx) => isGrad(ctx) || gradeAtLeast(ctx, 11),
    isPrimaryNav: true,
  },
  {
    id: "deadlines", path: "/deadlines", label: "Deadlines", icon: "\ud83d\udcc5",
    category: "application", description: "Track every important date",
    isVisible: (ctx) => isGrad(ctx) || gradeAtLeast(ctx, 11),
    isPrimaryNav: true,
  },
  {
    id: "rec-letters", path: "/rec-letters", label: "Rec Letters", icon: "\ud83d\udce8",
    category: "application", description: "Recommendation letter strategy",
    isVisible: (ctx) => isGrad(ctx) || gradeAtLeast(ctx, 11),
  },
  {
    id: "app-review", path: "/application-review", label: "App Review", icon: "\ud83d\udcda",
    category: "application", description: "Review your application before submitting",
    isVisible: (ctx) => isGrad(ctx) || gradeAtLeast(ctx, 12),
  },
  {
    id: "mock-interview", path: "/mock-interview", label: "Mock Interview", icon: "\ud83c\udf99\ufe0f",
    category: "application", description: "Practice with AI interviewer",
    isVisible: (ctx) => isGrad(ctx) || gradeAtLeast(ctx, 12),
  },
  {
    id: "interview-prep", path: "/interview-prep", label: "Interview Prep", icon: "\ud83d\udcda",
    category: "application", description: "Prepare for college interviews",
    isVisible: (ctx) => isGrad(ctx) || gradeAtLeast(ctx, 12),
  },
  {
    id: "social-audit", path: "/social-media-audit", label: "Social Audit", icon: "\ud83d\udd12",
    category: "application", description: "Clean up your online presence",
    isVisible: (ctx) => isUndergrad(ctx) && gradeAtLeast(ctx, 11),
  },
  {
    id: "decisions", path: "/decisions", label: "Decisions", icon: "\u2696\ufe0f",
    category: "application", description: "Compare offers and decide",
    isVisible: (ctx) => isGrad(ctx) || gradeAtLeast(ctx, 12),
  },

  // ─── Financial ─────────────────────────────────────────
  {
    id: "scholarships", path: "/scholarships", label: "Scholarships", gradLabel: "Fellowships", icon: "\ud83c\udfc6",
    category: "financial", description: "Find money for school",
    isVisible: (ctx) => isGrad(ctx) || gradeAtLeast(ctx, 11),
  },
  {
    id: "fafsa", path: "/fafsa-guide", label: "FAFSA Guide", icon: "\ud83d\udcb0",
    category: "financial", description: "Federal financial aid guide",
    isVisible: (ctx) => isUndergrad(ctx) && gradeAtLeast(ctx, 12),
  },

  // ─── Guidance ──────────────────────────────────────────
  {
    id: "chat", path: "/chat", label: "Ask Madrich", icon: "\ud83d\udcac",
    category: "guidance", description: "AI admissions advisor, 24/7",
    isVisible: () => true, isPrimaryNav: true,
  },
  {
    id: "counselor", path: "/counselor", label: "Counselor", icon: "\ud83e\uddd1\u200d\ud83c\udfeb",
    category: "guidance", description: "Guided counseling sessions",
    isVisible: () => true,
  },

  // ─── International ─────────────────────────────────────
  {
    id: "visa-guide", path: "/visa-guidance", label: "Visa Guide", icon: "\ud83c\udf0d",
    category: "international", description: "F-1 visa timeline and checklist",
    isVisible: (ctx) => ctx.isInternational && gradeAtLeast(ctx, 11),
  },
  {
    id: "location-intel", path: "/colleges", label: "Location Intel", icon: "\ud83d\udccd",
    category: "international", description: "Cost of living and campus life",
    isVisible: (ctx) => ctx.isInternational && gradeAtLeast(ctx, 11),
  },
  {
    id: "embassy-prep", path: "/visa-guidance", label: "Embassy Prep", icon: "\ud83c\udfe2",
    category: "international", description: "Prepare for your visa interview",
    isVisible: (ctx) => ctx.isInternational && gradeAtLeast(ctx, 12),
  },
  {
    id: "financial-docs", path: "/visa-guidance", label: "Financial Docs", icon: "\ud83d\udcc4",
    category: "international", description: "Required financial documentation",
    isVisible: (ctx) => ctx.isInternational && gradeAtLeast(ctx, 12),
  },

  // ─── Account ───────────────────────────────────────────
  {
    id: "profile", path: "/profile", label: "Profile", icon: "\ud83d\udc64",
    category: "account", description: "Your profile settings",
    isVisible: () => true,
  },
  {
    id: "billing", path: "/billing", label: "Billing", icon: "\ud83d\udcb3",
    category: "account", description: "Subscription and payments",
    isVisible: () => true,
  },
  {
    id: "support", path: "/support", label: "Support", icon: "\ud83c\udd98",
    category: "account", description: "Get help",
    isVisible: () => true,
  },
];

// ─── Public API ──────────────────────────────────────────

export function getVisibleFeatures(ctx: StudentContext): FeatureConfig[] {
  return FEATURES.filter((f) => f.isVisible(ctx));
}

export function getFeaturesByCategory(ctx: StudentContext): Record<FeatureCategory, FeatureConfig[]> {
  const visible = getVisibleFeatures(ctx);
  const result: Record<FeatureCategory, FeatureConfig[]> = {
    getting_started: [], planning: [], application: [], financial: [],
    guidance: [], international: [], account: [],
  };
  for (const f of visible) {
    result[f.category].push(f);
  }
  return result;
}

export function getPrimaryNavFeatures(ctx: StudentContext): FeatureConfig[] {
  return FEATURES.filter((f) => f.isPrimaryNav && f.isVisible(ctx));
}

export function getMoreMenuFeatures(ctx: StudentContext): FeatureConfig[] {
  return FEATURES.filter((f) => !f.isPrimaryNav && f.isVisible(ctx) && f.category !== "account");
}

export function getAccountFeatures(): FeatureConfig[] {
  return FEATURES.filter((f) => f.category === "account");
}

export function getLabel(feature: FeatureConfig, ctx: StudentContext): string {
  if (ctx.studentType === "GRADUATE" && feature.gradLabel) return feature.gradLabel;
  return feature.label;
}

const CATEGORY_META: Record<FeatureCategory, { label: string; icon: string }> = {
  getting_started: { label: "Getting Started", icon: "\ud83c\udfaf" },
  planning: { label: "Planning", icon: "\ud83d\udcda" },
  application: { label: "Application", icon: "\ud83d\udcdd" },
  financial: { label: "Financial", icon: "\ud83d\udcb0" },
  guidance: { label: "Guidance", icon: "\ud83d\udcac" },
  international: { label: "International", icon: "\ud83c\udf0d" },
  account: { label: "Account", icon: "\u2699\ufe0f" },
};

export { CATEGORY_META };
